import React, { useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import SortIcon from '@mui/icons-material/Sort';
import EmptyState from './EmptyState';
import SitterCard from './SitterCard';
import SitterCardSkeleton from './SitterCardSkeleton';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { UISitterDisplay } from '../../../../types/ui.sitter.types';

export type SortOption = 'recommended' | 'price-low' | 'price-high' | 'rating' | 'distance';

interface SittersListProps {
  sitters: UISitterDisplay[];
  totalSitters: number;
  selectedSitterId?: string | null;
  isLoading?: boolean;
  currentPage: number;
  pageSize: number;
  sortBy: SortOption;
  onSitterSelect?: (sitter: UISitterDisplay) => void;
  onViewProfile?: (sitter: UISitterDisplay) => void;
  onBookNow?: (sitter: UISitterDisplay) => void;
  onSortChange: (sort: SortOption) => void;
  onPageChange: (page: number) => void;
  onResetFilters?: () => void;
  setHandleShrink: (handleShrink: boolean) => void;
  handleShrink: boolean;
}

const SittersList: React.FC<SittersListProps> = (props: SittersListProps) => {
  const {
    sitters,
    totalSitters,
    selectedSitterId,
    isLoading = false,
    currentPage,
    pageSize,
    sortBy,
    onSitterSelect,
    onViewProfile,
    onBookNow,
    onSortChange,
    onPageChange,
    onResetFilters,
    setHandleShrink,
    handleShrink,
  } = props;

  const totalPages = Math.ceil(totalSitters / pageSize);
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalSitters);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    onPageChange(page);
    // Scroll to top of sitter list
    const sittersList = document.getElementById('sitters-list-container');
    if (sittersList) {
      sittersList.scrollTop = 0;
    }
  };

  const handleShrinkList = useCallback(() => {
    setHandleShrink(!handleShrink);
  }, [handleShrink, setHandleShrink]);

  const sittersListContent = useMemo(() => {
    if (isLoading) {
      return (
        <>
          {Array.from({ length: 5 }).map((_, index) => (
            <SitterCardSkeleton key={index} />
          ))}
        </>
      );
    }

    if (sitters.length > 0) {
      return (
        <Grid container spacing={2}>
          {sitters.map((sitter) => (
            <Grid key={sitter.id} size={handleShrink ? { xs: 12 } : { xs: 12, sm: 6 }}>
              <SitterCard
                sitter={sitter}
                isSelected={selectedSitterId === sitter.id}
                onCardClick={() => onSitterSelect?.(sitter)}
                onViewProfile={() => onViewProfile?.(sitter)}
                onBookNow={() => onBookNow?.(sitter)}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    return <EmptyState onResetFilters={onResetFilters} />;
  }, [
    handleShrink,
    isLoading,
    onBookNow,
    onResetFilters,
    onSitterSelect,
    onViewProfile,
    selectedSitterId,
    sitters,
  ]);

  const numberOfSitters = useMemo(() => {
    if (!isLoading && totalSitters > 0) {
      return (
        <Stack direction="row" gap={0.5}>
          <Typography variant="body1" sx={{ color: '#5A5A5A' }}>
            {isLoading ? 'Searching...' : `${totalSitters} sitter found`}
          </Typography>
          /
          <Typography variant="body1" sx={{ color: '#5A5A5A', display: 'block' }}>
            Showing {startIndex}-{endIndex} of {totalSitters}
          </Typography>
        </Stack>
      );
    }
  }, [endIndex, isLoading, startIndex, totalSitters]);

  return (
    <Box
      id="sitters-list-container"
      sx={{
        width: { xs: '100%', md: handleShrink ? 400 : 850 },
        // height: '92vh',
        height: '100%',
        overflowY: 'auto',
        backgroundColor: '#FAF8F5',
        p: 2,
        borderRight: { md: '1px solid #E0E0E0' },
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease-in-out',
      }}
    >
      {/* Header with Sort */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Available Sitters
            </Typography>
            {numberOfSitters}
          </Box>
          <Box
            onClick={handleShrinkList}
            sx={{
              width: 54,
              height: 54,
              backgroundColor: '#fff',
              borderRadius: 1,
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              border: '0.5px solid grey',
              transform: handleShrink ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s ease-in-out',
            }}
          >
            <ArrowBackIosIcon
              sx={{
                ml: 1,
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: 140,
            }}
          >
            <Select
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value as SortOption)}
              startAdornment={<SortIcon sx={{ mr: 0.5, fontSize: 18, color: '#5A5A5A' }} />}
              sx={{
                borderRadius: 2,
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  py: 0.75,
                },
              }}
            >
              <MenuItem value="recommended">Recommended</MenuItem>
              <MenuItem value="price-low">Price: Low to High</MenuItem>
              <MenuItem value="price-high">Price: High to Low</MenuItem>
              <MenuItem value="rating">Highest Rating</MenuItem>
              <MenuItem value="distance">Closest First</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      {sittersListContent}
      {/* Pagination */}
      {!isLoading && totalSitters > pageSize && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 2,
            borderTop: '1px solid #E0E0E0',
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                '&.Mui-selected': {
                  backgroundColor: '#178B8E',
                  '&:hover': {
                    backgroundColor: '#0F6164',
                  },
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default SittersList;
